import { create } from 'zustand';
import client from '../../apollo/client';
import { LIST_EVENTS, AUTHORIZED_DEALERS } from "../../apollo/queries/events";
interface Coordinates {
  latitude: number;
  longitude: number;
  __typename: string;
}

interface Dealer {
  id: string;
  address: string;
  city: string;
  name: string;
  phone: string;
  state: string;
  country: string;
  website: string;
  coordinates: Coordinates;
  __typename: string;
}

interface DealerConnection {
  count: number;
  nextToken: string | null;
  items: Dealer[];
  __typename: string;
}

interface ListDealersResponse {
  listDealers: DealerConnection;
}


interface Profile {
  name: string;
  owner: string;
  type: string;
  __typename: string;
}

/** Event row from listEvents (profile omitted in query — legacy Dynamo rows store it as a string). */
interface Event {
  eventName: string;
  description: string | null;
  eventType: string | null;
  sponsorshipTitle: string | null;
  id: string;
  image: string;
  eventLocation: string;
  date: string;
  country: string;
  city: string | null;
  coordinates: Coordinates | null;
  profile?: Profile | null;
  startDate: string;
  endDate: string;
  state: string | null;
  userId: string;
  visibility: string;
  durationMinutes: number | null;
  bufferMinutes: number | null;
  capacity: number | null;
  timezone: string | null;
  dailyStartTime: string | null;
  dailyEndTime: string | null;
  __typename: string;
}

interface EventConnection {
  items: Event[];
  nextToken?: string | null;
  count?: number | null;
  __typename: string;
}

interface ListEventsResponse {
  listEvents: EventConnection;
}

interface EventStore {
  toggle: boolean;
  eventToggle: boolean;
  openModalIndex: number | null;
  /** Set when guest clicks Book; cleared after login opens the booking modal. */
  pendingBookingIndex: number | null;
  selectedEvents: Event[];
  markerCluster: any | null;
  dealerMarkerCluster: any | null;
  selectedPlace: any | null;
  zoom: number;
  /** '' = show all event types; otherwise filter map markers to this eventType. */
  eventTypeFilter: string;
  eventsData: Event[];
  dealersData: Dealer[];
  isLoading: boolean;
  error: string | null;
  
  placeAutocomplete: any; 
  inputValue: string;
  currentMarker: any;
  activeMarker: any;
  setPlaceAutocomplete: (place: any) => void;
  setInputValue: (value: string) => void;
  setCurrentMarker: (marker: any) => void;
  setActiveMarker: (marker: any) => void;
  setToggle: (newToggle: boolean) => void;
  setZoom: (newZoom: number) => void;
  setEventToggle: (newEventToggle: boolean) => void;
  setOpenModalIndex: (index: number | null) => void;
  setPendingBookingIndex: (index: number | null) => void;
  setSelectedEvents: (events: Event[]) => void;
  setMarkerCluster: (markerCluster: any) => void;
  setDealerMarkerCluster: (dealerMarkerCluster: any) => void;
  setSelectedPlace: (selectedPlace: any) => void;
  setEventTypeFilter: (eventType: string) => void;
  fetchEventsData: () => Promise<void>;
  fetchDealersData: (user: any) => Promise<void>;
}

export const useEventStore = create<EventStore>((set) => ({
  toggle: true,
  eventToggle: true,
  openModalIndex: null,
  pendingBookingIndex: null,
  selectedEvents: [],
  markerCluster: null,
  dealerMarkerCluster: null,
  selectedPlace: null,
  zoom: 3,
  eventTypeFilter: "",
  eventsData: [],
  dealersData: [],
  isLoading: false,
  error: null,
  placeAutocomplete: null,
  inputValue: "",
  currentMarker: null,
  activeMarker: null,
  setToggle: (newToggle) => set({ toggle: newToggle }),
  setZoom: (newZoom) => set({ zoom: newZoom }),
  setEventToggle: (newEventToggle) => set({ eventToggle: newEventToggle }),
  setOpenModalIndex: (index) => set({ openModalIndex: index }),
  setPendingBookingIndex: (index) => set({ pendingBookingIndex: index }),
  setSelectedEvents: (events) => set({ selectedEvents: events }),
  setMarkerCluster: (markerCluster) => set({ markerCluster }),
  setDealerMarkerCluster: (dealerMarkerCluster) => set({ dealerMarkerCluster }),
  setSelectedPlace: (selectedPlace) => set({ selectedPlace }),
  setEventTypeFilter: (eventType) => set({ eventTypeFilter: eventType }),
  setPlaceAutocomplete: (place) => set({ placeAutocomplete: place }),
  setInputValue: (value) => set({ inputValue: value }),
  setCurrentMarker: (marker) => set({ currentMarker: marker }),
  setActiveMarker: (marker) => set({ activeMarker: marker }),

  handleError: (error: any) => {
    if (error instanceof Error) {
      return error.message;
    }
    return "An unknown error occurred.";
  },

  fetchEventsData: async () => {
    try {
      set({ isLoading: true, error: null });
      const { data, errors } = await client.query<ListEventsResponse>({
        query: LIST_EVENTS,
        // No country filter: values in DB are often "United States", not "USA"; ByCountry would return [].
        variables: {},
        fetchPolicy: "network-only",
        // Legacy Dynamo rows store coordinates as a string, which errors that field per item.
        // "all" keeps the rest of the list instead of failing the whole query.
        errorPolicy: "all",
      });
      const events = (Array.isArray(data?.listEvents?.items) ? data.listEvents.items : []).filter(
        Boolean
      );
      if (!events.length && errors?.length) {
        set({ error: errors[0]?.message ?? "Failed to load events.", isLoading: false });
        return;
      }
      set({ eventsData: events, isLoading: false });
    } catch (err: any) {
      set({ error: err.message, isLoading: false });
      console.error("Failed to fetch events data", err);
    }
  },

  fetchDealersData: async (user: any) => {
    try {
      if (!user) return;
      // Do not toggle global isLoading/error here — events fetch owns the /event page spinner.
      const { data } = await client.query<ListDealersResponse>({
        query: AUTHORIZED_DEALERS,
        variables: {},
        fetchPolicy: "network-only",
      });
      const dealers = Array.isArray(data?.listDealers.items) ? data?.listDealers.items : [];
      set({ dealersData: dealers });
    } catch (err: any) {
      console.error("Failed to fetch dealers data", err);
      set({ dealersData: [] });
    }
  },
}));

export const useEventActions = () =>
  useEventStore((s) => ({
    setSelectedEvents: s.setSelectedEvents,
    setOpenModalIndex: s.setOpenModalIndex,
    setPendingBookingIndex: s.setPendingBookingIndex,
  }));

export const useEventStates = () =>
  useEventStore((s) => ({
    selectedEvents: s.selectedEvents,
  }));